import { HttpClient, HttpHeaders } from '@angular/common/http';
import { AfterViewChecked, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { environment } from '../../../../../environments/environment';
import { FormsModule } from '@angular/forms';
import { SvgIconComponent } from '../../../../services/svg-service/svg-icon/svg-icon';
import { TranslationService } from '../../../../services/translation.service';

interface Message {
  id: string;
  content: string;
  isUser: boolean;
  isStreaming?: boolean;
  isLoading?: boolean;
  timestamp: Date;
}

@Component({
  selector: 'app-msaada-ai',
  imports: [FormsModule, SvgIconComponent],
  templateUrl: './msaada-ai.html',
  styleUrl: './msaada-ai.scss',
})
export class MsaadaAi implements OnInit, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;
  private readonly http = inject(HttpClient);
  public readonly translationService = inject(TranslationService);

  sessionId: string | null = null;
  locale: string = 'sw';
  initialtext: string = '';
  placeholderText: string = '';
  messages: Message[] = [];
  inputMessage = '';

  headers: HttpHeaders = new HttpHeaders({
    Authorization: `Bearer ${environment.MSAADA_AI_BEARER_TOKEN}`,
    Accept: 'text/event-stream',
  });

  ngOnInit(): void {
    this.locale = this.translationService.getLanguageSignal()();
    if (this.locale !== 'sw') {
      this.initialtext = 'Preparing your answer...';
      this.placeholderText = 'Type your message here...';
    } else {
      this.initialtext = 'Tunaandaa majibu...';
      this.placeholderText = 'Andika ujumbe wako hapa...';
    }
    this.InitiateConversions();
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  InitiateConversions() {
    const body = { question: '', locale: this.locale };

    const welcomeId = this.generateId();
    this.messages.push({
      id: welcomeId,
      content: this.initialtext,
      isUser: false,
      isLoading: true,
      timestamp: new Date(),
    });

    this.http
      .post(environment.MSAADA_AI_COMPLETIONS_ENDPOINT as string, body, {
        headers: this.headers,
        responseType: 'text',
        observe: 'body',
      })
      .subscribe({
        next: (response) => {
          this.sessionId = this.extractSessionId(response);
          const welcomeMessage = this.extractWelcomeMessage(response);

          this.replaceAndStreamResponse(welcomeId, welcomeMessage);
        },
        error: (err) => {
          console.error('Error:', err);
          const msg = this.messages.find((m) => m.id === welcomeId);
          if (msg) {
            msg.content =
              'Habari! Karibu kwenye Msaada AI. Samahani, kuna hitilafu ya kiunganishi.';
            msg.isLoading = false;
          }
        },
      });
  }

  async sendMessageStream() {
    if (!this.inputMessage.trim() || !this.sessionId) return;

    const userMessage: Message = {
      id: this.generateId(),
      content: this.inputMessage,
      isUser: true,
      timestamp: new Date(),
    };
    this.messages.push(userMessage);

    const question = this.inputMessage;
    this.inputMessage = '';

    const aiMessageId = this.generateId();
    this.messages.push({
      id: aiMessageId,
      content: '',
      isUser: false,
      isLoading: true,
      isStreaming: false,
      timestamp: new Date(),
    });

    const body = {
      question: question,
      session_id: this.sessionId,
    };

    this.http
      .post(environment.MSAADA_AI_COMPLETIONS_ENDPOINT as string, body, {
        headers: this.headers,
        responseType: 'text',
      })
      .subscribe({
        next: (response) => {
          const finalAnswer = this.extractFinalAnswer(response);
          this.replaceAndStreamResponse(aiMessageId, finalAnswer);
        },
        error: (err) => {
          console.error('Error:', err);
          const msg = this.messages.find((m) => m.id === aiMessageId);
          if (msg) {
            msg.content = 'Samahani, kuna hitilafu. Tafadhali jaribu tena.';
            msg.isLoading = false;
            msg.isStreaming = false;
          }
        },
      });
  }

  private replaceAndStreamResponse(messageId: string, fullText: string) {
    const message = this.messages.find((m) => m.id === messageId);
    if (!message) return;

    message.content = '';
    message.isLoading = false;
    message.isStreaming = true;

    const chars = fullText.split('');
    let currentIndex = 0;

    const interval = setInterval(() => {
      if (currentIndex < chars.length) {
        message.content += chars[currentIndex];
        currentIndex++;
      } else {
        clearInterval(interval);
        setTimeout(() => {
          message.isStreaming = false;
        }, 400);
      }
    }, 25);
  }

  private extractWelcomeMessage(response: string): string {
    const lines = response.split('\n\n');

    for (const line of lines) {
      if (line.startsWith('data:') && line.includes('"answer"')) {
        try {
          const jsonStr = line.replace('data:', '').trim();
          const parsed = JSON.parse(jsonStr);
          if (parsed.data?.answer) {
            return parsed.data.answer;
          }
        } catch (e) {
          console.error('Error parsing welcome message:', e);
        }
      }
    }
    return 'Habari! Karibu kwenye Msaada AI. Mimi ni msaidizi wako wa kidigitali.';
  }

  private extractSessionId(response: string): string | null {
    const match = response.match(/"session_id":\s*"([^"]+)"/);
    return match ? match[1] : null;
  }

  private extractFinalAnswer(response: string): string {
    const lines = response.split('\n\n');
    let lastAnswer = '';

    for (const line of lines) {
      if (line.startsWith('data:') && !line.includes('"data": true')) {
        try {
          const jsonStr = line.replace('data:', '').trim();
          const parsed = JSON.parse(jsonStr);
          if (parsed.data?.answer && parsed.data.answer.length > lastAnswer.length) {
            lastAnswer = parsed.data.answer;
          }
        } catch (e) {
          console.error('Error parsing final answer:', e);
        }
      }
    }

    return lastAnswer || 'Samahani, sijaelewa.';
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2, 9);
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessageStream();
    }
  }

  private scrollToBottom(): void {
    if (this.messagesContainer) {
      this.messagesContainer.nativeElement.scrollTop =
        this.messagesContainer.nativeElement.scrollHeight;
    }
  }
}
